import type { InterpolationValue } from 'styled-components'
import styled, { css } from 'styled-components'
import { colors } from 'config/ui'
import ellipsis from 'polished/lib/mixins/ellipsis'
import { tokens, Modal } from '@arvoreeducacao/bonsai-ds'
import { ButtonBack } from 'screens/site/activities/Activities.styles'

export const ContainerOnlySmallScreens = styled.div<{ display?: string }>`
  display: none;
  width: 100%;

  @media (max-width: ${tokens.breakpoints.desktop}px) {
    justify-content: center;
    display: ${props => props.display || 'flex'};
  }
`

export const ContainerOnlyBigScreens = styled.div<{ display?: string }>`
  width: 100%;
  display: ${props => props.display || 'flex'};

  @media (max-width: ${tokens.breakpoints.desktop}px) {
    display: none;
  }
`

export const FlexSpaceBetween = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: normal;
`

export const AdminPanelGrid = styled.div`
  margin-top: 50px;
`

export const AdminPanelRow = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: baseline;

  > div {
    margin: 0 20px 20px 0;
  }

  > div:last-child {
    margin-right: 0;
  }
`

export const AdminTitleContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 50px;
  align-items: baseline;

  @media (min-width: ${tokens.sizes.lgScreen}px) {
    flex-direction: row;
    padding-left: 100px;
  }
`

export const AdminTitle = styled.div<{
  noBorder?: boolean
  noPadding?: boolean
}>`
  ${({ noBorder, noPadding }) => css`
    ${noBorder
      ? 'border: 0 none;'
      : `border-bottom: 1px solid ${colors.lightGray}`};
    ${noPadding ? 'padding: 0;' : `padding: 50px;`}
  `};

  font-size: 28px;
  color: ${colors.black};

  @media (min-width: 1920px) {
    ${({ noPadding }) => css`
      ${noPadding ? 'padding: 0;' : `padding: 100px;`}
    `};
  }
`

export const AdminSubtitle = styled.p`
  font-size: ${tokens.sizes.bodySmall}px;
  font-weight: ${tokens.fontWeights.regular};
  color: ${tokens.colors.black5};
  line-height: 16px;
  text-align: center;
  margin: 0 0 18px 20px;
  max-width: 263px;

  @media (min-width: ${tokens.sizes.mdScreen}px) {
    font-size: ${tokens.sizes.text}px;
    line-height: 28px;
    margin: 0 0 32px 20px;
    max-width: 294px;
  }
`

export const TextWithCircleBefore = styled.div<{ textColor?: string }>`
  ${({ color, textColor }) => css`
    display: flex;
    align-items: center;
    ${textColor && `color: ${textColor};`} &:before {
      content: '';
      display: flex;
      height: 10px;
      width: 10px;
      border-radius: 50%;
      background-color: ${color || colors.gray};
      margin-right: 10px;
    }
  `};
`

export const AdminFilters = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;

  > div:not(:last-child) {
    margin-right: 15px;
  }
`

export const WrapperWithMarginTop = styled.div`
  margin-top: 50px;
`

export const TitleAndSubtitleTableCell = styled.div`
  display: flex;
  flex-direction: column;
  > span {
    color: ${colors.gray};
    font-size: ${tokens.fontSize.small};
    margin-top: 10px;
  }

  > div {
    @media (max-width: 1400px) {
      ${ellipsis('170px') as InterpolationValue[]};
    }
  }
`

export const LevelTableCell = styled(TextWithCircleBefore)`
  text-transform: capitalize;
`

export const ActionsTableCell = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-right: 20px;
`

export const UserInfoTableCell = styled.div`
  display: flex;
  align-items: center;

  > span {
    margin-left: 10px;

    > span {
      margin-left: 10px;
      background-color: ${colors.blue};
      padding: 2px 5px;
      color: ${tokens.colors.white};
      border-radius: 5px;
      font-size: 11px;
      font-weight: 800;
    }
  }
`

export const PrevIconWrapper = styled.div`
  color: ${tokens.colors.primary};
  cursor: pointer;
  font-size: 28px;
`

export const ReportsTitleWrapper = styled.div`
  margin: 50px;
  display: flex;
  flex-direction: column;
  gap: ${tokens.spacing.insetSmall};

  @media (min-width: 1920px) {
    margin-left: 100px;
  }
`

export const RightJustified = styled.div`
  text-align: right;
  margin-right: 20px;
`

export const EffectiveReadWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin-right: 20px;

  > div {
    margin-left: 20px;
  }
`

export const TableHeaderColumnWrapper = styled.div<{ justifyRight?: boolean }>`
  ${({ justifyRight }) => css`
    display: flex;
    align-items: center;
    cursor: pointer;
    padding: 0 10px;
    flex: 1 !important;
    ${justifyRight &&
    `
      justify-content: flex-end;
      margin-right: 20px;
    `} svg {
      margin-left: 10px;
    }

    @media not print (max-width: 1400px) {
      ${ellipsis('150px') as InterpolationValue[]};
    }

    @media print {
      svg {
        display: none;
      }
    }
  `};
`

export const PageWrapperCentralized = styled.div`
  min-height: 100vh;
  width: 100vw;
  padding: 20px 0;
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: center;
`

export const PageTitle = styled.h1`
  margin-top: 35px;
  margin-bottom: 35px;

  @media (max-width: ${tokens.breakpoints.tablet}px) {
    margin-left: 20px;
  }
`

export const Place = styled.div<{ place?: number }>`
  ${({ place }) => css`
    min-height: 45px;
    min-width: 45px;
    max-width: 45px;
    border-radius: 50%;
    display: flex;
    justify-content: center;
    align-items: center;
    text-transform: uppercase;
    font-weight: bold;
    ${place === 1
      ? `
        color: #eccf43;
        background-color: #fff5df;
      `
      : `
        color: ${colors.avatarText};
        background-color: ${colors.avatarBg};
      `};
  `};
`

export const SimpleButton = styled.button`
  -webkit-appearance: none;
  appearance: none;
  border: 0 none;
  cursor: pointer;
  background: transparent;
  outline: none;

  &:focus-visible {
    outline: 2px solid ${tokens.colors.dark6};
    outline-offset: 2px;
  }
`

// Activities Model
export const FullPageWrapper = styled.div`
  background: ${tokens.colors.blackBg};
  width: 100vw;
  min-height: 100vh;
  padding: 0 ${tokens.spacing.insetMedium};
`

export const WhiteBoxWithShadow = styled.div`
  background: ${tokens.colors.white};
  margin-bottom: 32px;

  width: calc(100% + 10px);
  position: relative;
  left: -5px;

  @media (min-width: ${tokens.breakpoints.desktop}px) {
    border-radius: 3px;
    border: 1px solid #dfe5e9;
    box-shadow: 0 0 15px 2px rgba(83, 101, 111, 0.1);
    width: 100%;
    left: 0;
  }
`

export const ActivityModelContainerQuestion = styled.div`
  padding: 16px 16px 32px 16px;
  border-top: 1px solid #dfe5e9;

  &:first-child {
    border: none;
  }

  h3 {
    color: ${tokens.colors.black6};
    margin: 0 0 32px 0;
  }

  @media (min-width: ${tokens.breakpoints.desktop}px) {
    padding: 40px 32px 56px 32px;
  }
`

export const ActivityModelTitleWrapper = styled.div`
  padding: ${tokens.spacing.insetLarge} 0;

  @media (min-width: ${tokens.breakpoints.desktop}px) {
    padding: ${tokens.spacing.verticalXxlarge} 0
      ${tokens.spacing.verticalXlarge};
  }

  h1 {
    margin: 0 0 8px 0;
    color: ${tokens.colors.black6};
  }

  p {
    font-size: ${tokens.fontSize.medium};
    line-height: 24px;
    color: ${colors.gray7};
    margin: 0;
  }
`

export const ActivityModelContentChoiceCreated = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: flex-start;
  margin-top: 24px;
`

export const ActivityModelLabel = styled.div`
  font-size: ${tokens.fontSize.small};
  font-weight: 700;
  color: ${colors.gray7};
  margin-bottom: 8px;
`

export const ActivityModelAnswerContent = styled.div`
  margin-top: 24px;

  .quill {
    background: #fff;
  }
`

export const ActivityModelCommentContentReady = styled.div`
  background: #fff1b6;
  margin-top: 24px;
  padding: 24px;
  border-radius: 3px;
`

export const ActivityModelCommentContentEdit = styled.div`
  margin-top: 24px;
  padding: 24px;
  border-radius: 3px;
  background: #fff1b6;

  .quill {
    background: #fff;
  }
`

export const ActivityModelAnswerContentReady = styled.div`
  background: #dfe5e9;
  padding: 24px;
  margin-top: 24px;
  border-radius: 3px;
`

export const ActivityModelAnswerContentEdit = styled.div`
  background: #dfe5e9;
  padding: 24px;
  margin-top: 24px;
  border-radius: 3px;
  .quill {
    background: #fff;
  }
`

export const ActivityModelFooter = styled.div`
  margin: 32px 0 60px 0;
  display: flex;
  flex-direction: row-reverse;
  align-items: center;
  justify-content: flex-start;

  > button {
    margin-bottom: 16px;
    align-items: center;
    justify-content: center;
    margin-left: 0 !important;
  }

  @media (min-width: ${tokens.breakpoints.desktop}px) {
    > button {
      width: inherit;
      margin-bottom: 0;
      align-items: inherit;
      justify-content: inherit;
      margin-left: 16px !important;
    }
  }

  > ${ButtonBack} {
    margin-right: auto;
  }
`

export const ActivityResultContent = styled.div`
  width: 100%;
  background: ${tokens.colors.primary};
  border-radius: 3px;
  padding: 40px 32px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin-bottom: 24px;

  h2,
  h4,
  p {
    color: #fff;
    margin-top: 0;
  }

  h4 {
    margin-bottom: 16px;
  }
`

// end activities model

export const LateralToggleBar = styled.div<{ show?: boolean }>`
  position: fixed;
  padding: 5px;
  bottom: 0px;
  width: 100%;
  height: 32px;
  left: 0px;
  background: #58d68d;
  z-index: 10;
  opacity: 0;
  color: ${tokens.colors.white};
  transition: all 250ms ease;
  ${props =>
    props.show
      ? 'transform: translateY(0); opacity: 1;'
      : 'transform: translateY(calc(100% + 20px)); opacity: 0;'};

  > .contLateralToggleBar {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  @media (min-width: ${tokens.breakpoints.desktop}px) {
    width: inherit;
    padding: 20px;
    height: inherit;
    bottom: 20px;
    left: 20px;

    > .contLateralToggleBar {
      align-items: center;
      justify-content: flex-start;
    }
  }
`

export const ClientManagementContentHeaderWrapper = styled.div`
  margin: ${tokens.spacing.spacing_40}px 0 ${tokens.spacing.spacing_32}px 0;
`

export const StyledModal = styled(Modal)`
  @media (max-width: ${tokens.sizes.smScreen}px) {
    position: absolute;
    top: 100px;
    max-height: 242px;
  }

  > div {
    min-height: 222px;

    @media (max-width: ${tokens.sizes.smScreen}px) {
      &:first-child {
        display: none;
      }

      width: 80%;
      margin: 0 auto;
      padding: ${tokens.spacing.insetXlarge};
      border-radius: ${tokens.radius.xlarge};
    }
  }

  h2 {
    font-size: ${tokens.fontSize.large};
    color: ${tokens.colors.dark6};
  }
`

export const ContentFooterModal = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
`

export const ModalDescription = styled.p`
  font-size: ${tokens.fontSize.medium};
  color: ${tokens.colors.dark5};
`

export const FlexContainer = styled.div<{
  width?: string
  justify?: string
  align?: string
  gap?: string
  direction?: string
}>`
  display: flex;
  width: ${props => props.width || '100%'};
  justify-content: ${props => props.justify || 'flex-start'};
  align-items: ${props => props.align || 'center'};
  gap: ${props => props.gap || 'normal'};
  flex-direction: ${props => props.direction || 'row'};
`
